import { SomethingWentWrong } from '@/constants/errorMessages';
import { useAuth } from '@/context/Auth';
import Axios from '@/lib/axiosConfig';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

// TODO, merge both
const useGroupSubscriptionMutation = (groupId: string, options: any) => {};

export const useLeaveCommunityMutation = (communityId: string) => {
  const queryClient = useQueryClient();
  const { getAPIRequestTokenHeaders, user } = useAuth();

  const leaveCommunityMutation = useMutation({
    mutationKey: [`subscriptions/${communityId}`],
    mutationFn: (targetUserID?: string) =>
      Axios.delete('/subscriptions', {
        data: {
          groupID: communityId,
          targetUserID: targetUserID ?? user.sub,
        },
        headers: getAPIRequestTokenHeaders(),
      }),

    onSuccess: (data, userID) => {
      toast.success(userID ? 'Removed!' : 'UnSubscribed!');
      if (userID) {
        queryClient.invalidateQueries({
          queryKey: ['group', communityId, 'members'],
          exact: false,
        });
      }
      queryClient.invalidateQueries({
        queryKey: [`users/${user.sub}/subscriptions`],
      });
      queryClient.invalidateQueries({
        queryKey: ['communities/my'],
      });
    },
    onError: e => {
      toast.error(e?.message ?? SomethingWentWrong);
    },
    onMutate: () => {
      queryClient.setQueriesData(
        {
          exact: false,
          queryKey: ['posts', 'infinite'],
        },
        oldData => {
          return {
            ...oldData,
            pages: oldData.pages.map(page => {
              return {
                ...page,
                responseObject: {
                  ...page.responseObject,
                  rows: page.responseObject.rows.map(row => {
                    if (row.communityID === communityId) {
                      return {
                        ...row,
                        isMember: false,
                      };
                    }
                    return row;
                  }),
                },
              };
            }),
          };
        }
      );
    },
  });
  return leaveCommunityMutation;
};

export const useJoinCommunityMutation = (communityId: string) => {
  const queryClient = useQueryClient();
  const { getAPIRequestTokenHeaders, user } = useAuth();

  const joinCommunityMutation = useMutation({
    mutationKey: [`subscriptions/${communityId}`],
    mutationFn: (targetUserID?: string) =>
      Axios.post(
        '/subscriptions',
        {
          groupID: communityId,
          targetUserID: (typeof targetUserID === 'object' ? targetUserID?.source : targetUserID) ?? user.sub,
        },
        {
          headers: getAPIRequestTokenHeaders(),
        }
      ),
    onSuccess: () => {
      toast.success('Subscribed!');
      queryClient.invalidateQueries({
        queryKey: [`users/${user.sub}/subscriptions`],
      });
      queryClient.invalidateQueries({
        queryKey: ['communities/my'],
      });
    },
    onError: e => {
      toast.error(e?.message ?? SomethingWentWrong);
    },
    onMutate: () => {
      queryClient.setQueriesData(
        {
          exact: false,
          queryKey: ['posts', 'infinite'],
        },
        oldData => {
          return {
            ...oldData,
            pages: oldData.pages.map(page => {
              return {
                ...page,
                responseObject: {
                  ...page.responseObject,
                  rows: page.responseObject.rows.map(row => {
                    if (row.communityID === communityId) {
                      return {
                        ...row,
                        isMember: true,
                      };
                    }
                    return row;
                  }),
                },
              };
            }),
          };
        }
      );
    },
  });
  return joinCommunityMutation;
};
export const useToggleNotificationMutation = (communityId: string) => {
  const queryClient = useQueryClient();
  const { getAPIRequestTokenHeaders, user } = useAuth();

  const toggleNotificationMutation = useMutation({
    mutationKey: [`subscriptions/notification`],

    mutationFn: (notificationEnabled: boolean) =>
      Axios.patch(
        '/subscriptions/notification',
        {
          groupID: communityId,
          notificationEnabled,
        },
        {
          headers: getAPIRequestTokenHeaders(),
        }
      ),

    onSuccess: () => {
      toast.success(' Group notification updated!');
      queryClient.invalidateQueries({
        queryKey: [`users/${user.sub}/subscriptions`],
      });
    },

    onError: e => {
      toast.error(e?.message ?? 'Something went wrong');
    },

    onMutate: (notificationEnabled: boolean) => {
      queryClient.setQueriesData(
        {
          exact: false,
          queryKey: [`users/${user.sub}/subscriptions`],
        },
        (old: any) => {
          if (!old?.responseObject?.rows) return old;
    
          return {
            ...old,
            responseObject: {
              ...old.responseObject,
              rows: old.responseObject.rows.map(sub =>
                sub.id === communityId
                  ? { ...sub, notificationEnabled }
                  : sub
              ),
            },
          };
        }
      );
    }
    
    
  });

  return toggleNotificationMutation;
};
